package org.maplibre.compose.demoapp.demos

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.gestures.detectDragGestures
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.offset
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.MaterialTheme
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableDoubleStateOf
import androidx.compose.runtime.mutableFloatStateOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberUpdatedState
import androidx.compose.runtime.setValue
import androidx.compose.runtime.snapshotFlow
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.draw.shadow
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.Shape
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.layout.LayoutCoordinates
import androidx.compose.ui.layout.onGloballyPositioned
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.unit.DpOffset
import androidx.compose.ui.unit.IntOffset
import androidx.compose.ui.unit.dp
import kotlin.math.roundToInt
import org.maplibre.compose.demoapp.DefaultMapControls
import org.maplibre.compose.demoapp.Demo
import org.maplibre.compose.demoapp.DemoAppState
import org.maplibre.compose.demoapp.DemoDestination
import org.maplibre.compose.demoapp.DemoMapControls
import org.maplibre.compose.demoapp.center
import org.maplibre.compose.demoapp.controlPadding
import org.maplibre.compose.demoapp.design.SectionHeader
import org.maplibre.compose.demoapp.design.SegmentedRow
import org.maplibre.compose.demoapp.design.SliderRow
import org.maplibre.compose.demoapp.design.SwitchRow
import org.maplibre.compose.interaction.MapInteractions
import org.maplibre.compose.map.MapUiOptions
import org.maplibre.compose.map.MaplibreMap
import org.maplibre.compose.map.rememberMapState
import org.maplibre.compose.overlay.MapOverlayScope

/**
 * A second map floats over the shared one as a magnifying lens.
 *
 * The lens has its own logical map, synced one-way from the main map. The lens map disables
 * interactions so the sync cannot loop.
 *
 * On Android, Compose modifiers reach the map only in texture mode, so the panel exposes the lens
 * map's render mode.
 */
object MagnifyingLensDemo : Demo {
  override val name = "Magnifying lens"
  override val description =
    "Drag a magnifying lens over the map. Change its size, shape, and magnification."

  override val destination = DemoDestination.None

  private enum class LensShape(val label: String, val shape: Shape) {
    Circle("Circle", CircleShape),
    Square("Rounded square", RoundedCornerShape(24.dp)),
  }

  private val rimBrush =
    Brush.sweepGradient(
      listOf(Color(0xFF9E9E9E), Color(0xFFF5F5F5), Color(0xFF616161), Color(0xFF9E9E9E)),
    )

  /** Each zoom level added doubles the scale, hence the 2×/4×/8× labels. */
  private var magnification by mutableDoubleStateOf(1.0)
  private var lensSize by mutableFloatStateOf(220f)
  private var lensShape by mutableStateOf(LensShape.Circle)
  private var dragOffset by mutableStateOf(Offset.Zero)
  private var lensUiOptions by mutableStateOf(LensUiOptionsDefault)
  private var lensDistortionEnabled by mutableStateOf(true)

  @Composable
  override fun MapOverlayScope.Overlay(state: DemoAppState, controls: DemoMapControls) {
    DemoOverlay(state)
    DefaultMapControls(controls)
  }

  @Composable
  private fun DemoOverlay(state: DemoAppState) {
    val mapState = state.mapState
    val appliedStyle = state.appliedStyle
    val lensState = rememberMapState(runtime = state.mapRuntime, baseStyle = appliedStyle.base)
    val density = LocalDensity.current
    val lensSizePx = with(density) { lensSize.dp.toPx() }
    var overlayCoordinates by remember { mutableStateOf<LayoutCoordinates?>(null) }
    var controlsCenter by remember { mutableStateOf<Offset?>(null) }
    val placedDrag = IntOffset(dragOffset.x.roundToInt(), dragOffset.y.roundToInt())
    val lensCenter = controlsCenter?.plus(Offset(placedDrag.x.toFloat(), placedDrag.y.toFloat()))
    val currentLensCenter by rememberUpdatedState(lensCenter)
    LaunchedEffect(Unit) {
      snapshotFlow {
        val position = mapState.cameraPosition
        val target =
          currentLensCenter?.let {
            mapState.viewport?.positionFromScreenLocation(
              with(density) { DpOffset(it.x.toDp(), it.y.toDp()) },
            )
          } ?: position.center
        position.copy(center = target, zoom = position.zoom + magnification)
      }
        .collect { lensState.setCameraPosition(it) }
    }

    Box(Modifier.fillMaxSize().onGloballyPositioned { overlayCoordinates = it }) {
      Box(Modifier.fillMaxSize().controlPadding()) {
        Box(
          Modifier.fillMaxSize().onGloballyPositioned { coordinates ->
            controlsCenter =
              overlayCoordinates?.localBoundingBoxOf(coordinates, clipBounds = false)?.center
          },
        ) {
          Box(
            modifier =
              Modifier.align(Alignment.Center)
                .offset { placedDrag }
                .pointerInput(Unit) {
                  detectDragGestures { change, dragAmount ->
                    change.consume()
                    dragOffset += dragAmount
                  }
                }
                .size(lensSize.dp)
                .shadow(16.dp, lensShape.shape)
                .border(6.dp, rimBrush, lensShape.shape)
                .clip(lensShape.shape)
                .background(MaterialTheme.colorScheme.surfaceVariant),
          ) {
            MaplibreMap(
              modifier =
                if (lensDistortionEnabled) {
                  Modifier.fillMaxSize().radialLensDistortion(lensSizePx)
                } else {
                  Modifier.fillMaxSize()
                },
              state = lensState,
              uiOptions = lensUiOptions,
              interactions = MapInteractions.None,
            ) {}
            Box(
              Modifier.fillMaxSize()
                .background(
                  Brush.linearGradient(
                    0.0f to Color.White.copy(alpha = 0.30f),
                    0.4f to Color.White.copy(alpha = 0.05f),
                    0.6f to Color.Transparent,
                  ),
                ),
            )
          }
        }
      }
    }
  }

  @Composable
  override fun PeekPanel(state: DemoAppState) {
    SegmentedRow(
      label = "Magnification",
      options = listOf(1.0, 2.0, 3.0),
      selected = magnification,
      optionLabel = { "${1 shl it.toInt()}×" },
      onSelect = { magnification = it },
    )
  }

  @Composable
  override fun Panel(state: DemoAppState) {
    LensRenderSection(lensUiOptions) { lensUiOptions = it }

    SectionHeader("Lens")
    SwitchRow("Lens distortion", lensDistortionEnabled) { lensDistortionEnabled = it }
    SegmentedRow(
      label = "Shape",
      options = LensShape.entries,
      selected = lensShape,
      optionLabel = { it.label },
      onSelect = { lensShape = it },
    )
    SliderRow("Size", lensSize, 128f..360f) { lensSize = it }
  }
}

/**
 * The lens map's render mode row. Android presents the map as a texture or as a surface; every
 * other platform has one presentation, so the actuals there are empty.
 */
@Composable
expect fun LensRenderSection(lensOptions: MapUiOptions, onLensChange: (MapUiOptions) -> Unit)

/**
 * The lens map's initial render options: texture mode where a texture-versus-surface choice exists,
 * because Android applies Compose modifiers to the map only in texture mode.
 */
expect val LensUiOptionsDefault: MapUiOptions

/** Applies a convex-lens distortion where the platform supports runtime shaders. */
@Composable expect fun Modifier.radialLensDistortion(sizePx: Float): Modifier

internal const val LensShader =
  """
  uniform shader content;
  uniform float2 size;

  half4 main(float2 coord) {
    float2 center = size * 0.5;
    float2 delta = coord - center;
    float radius = min(size.x, size.y) * 0.5;
    float distanceFromCenter = min(length(delta) / radius, 1.0);
    float scale = 0.82 + 0.18 * distanceFromCenter * distanceFromCenter;
    return content.eval(center + delta * scale);
  }
"""
